import { Injectable } from '@angular/core';
import { BehaviorSubject, interval, Subscription } from 'rxjs';
import { Router } from '@angular/router';
import { CreditWallet } from '@modules/credit-wallet/Domain/Entities/credit-wallet.entity';
import { CreditWalletManagementApolloRepository } from '@modules/credit-wallet/Infrastructure/Repositories/credit-wallet-management-apollo.repository';
import { DisplayMode } from '@modules/credit-wallet/Domain/display-mode';

const POLL_INTERVAL_MS = 60_000;

/**
 * CreditBadgeService — lightweight singleton that holds wallet state for the topbar badge.
 * Separate from CreditWalletFacade (which is scoped to the page component).
 * Uses BehaviorSubject (not signals) so the component stays compatible with NgModule hosts.
 *
 * Uses CreditWalletManagementApolloRepository (null-safe) so that a tenant with no wallet
 * yields walletChecked=true + wallet=null, letting the badge hide itself cleanly.
 */
@Injectable({ providedIn: 'root' })
export class CreditBadgeService {
  private readonly _wallet$        = new BehaviorSubject<CreditWallet | null>(null);
  private readonly _loading$       = new BehaviorSubject(false);
  private readonly _displayMode$   = new BehaviorSubject<DisplayMode>('credits');
  private readonly _walletChecked$ = new BehaviorSubject<boolean>(false);
  private pollSub: Subscription | null = null;

  readonly wallet$        = this._wallet$.asObservable();
  readonly loading$       = this._loading$.asObservable();
  readonly displayMode$   = this._displayMode$.asObservable();
  readonly walletChecked$ = this._walletChecked$.asObservable();

  getDisplayMode(): DisplayMode { return this._displayMode$.value; }
  setDisplayMode(mode: DisplayMode): void { this._displayMode$.next(mode); }

  constructor(
    private readonly repo: CreditWalletManagementApolloRepository,
    private readonly router: Router,
  ) {}

  private get tenantId(): string {
    return this.router.parseUrl(this.router.url).queryParams['tenant'] ?? '';
  }

  /** Initial load — skips if wallet for this tenant is already loaded. Shows spinner. */
  load(): void {
    const tenantId = this.tenantId;
    if (!tenantId) return;
    if (this._loading$.value) return;
    if (this._wallet$.value?.tenantId === tenantId) return;

    this._walletChecked$.next(false);
    this._loading$.next(true);
    this.repo.getByTenantId(tenantId).subscribe({
      next: (wallet) => {
        this._wallet$.next(wallet);
        this._walletChecked$.next(true);
        this._loading$.next(false);
      },
      error: () => { this._loading$.next(false); },
    });
  }

  /** Refresh with loading indicator — always fetches (used by the poller and after writes). */
  refresh(): void {
    const tenantId = this.tenantId;
    if (!tenantId) return;
    if (this._loading$.value) return;

    this._loading$.next(true);
    this.repo.getByTenantId(tenantId).subscribe({
      next: (wallet) => {
        this._wallet$.next(wallet);
        this._walletChecked$.next(true);
        this._loading$.next(false);
      },
      error: () => { this._loading$.next(false); },
    });
  }

  startPolling(intervalMs = POLL_INTERVAL_MS): void {
    this.stopPolling();
    this.pollSub = interval(intervalMs).subscribe(() => this.refresh());
  }

  stopPolling(): void {
    this.pollSub?.unsubscribe();
    this.pollSub = null;
  }
}
